import { Autocomplete, TextField } from "@mui/material";

/** Código de un catálogo: numérico (ciudades, barrios) o texto (departamentos DANE, "05"). */
export type CodigoCatalogo = number | string;

export interface OpcionCatalogo<C extends CodigoCatalogo = number> {
  codigo: C;
  nombre: string;
}

interface CampoAutocompletarProps<C extends CodigoCatalogo> {
  label: string;
  opciones: OpcionCatalogo<C>[];
  /** Código seleccionado, o "" si no hay selección. */
  valor: C | "";
  onCambio: (codigo: C | "") => void;
  cargando?: boolean;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  textoSinOpciones?: string;
}

/** Quita tildes y pasa a minúsculas: "Peñitas" → "penitas". */
const normalizar = (texto: string) =>
  texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/**
 * Lista desplegable con buscador para catálogos (departamentos, municipios,
 * barrios…). El usuario escribe parte del nombre, sin importar tildes ni
 * mayúsculas, y elige; el formulario recibe el código. Las opciones se
 * muestran en el orden en que llegan (p. ej. la capital de primera).
 */
export const CampoAutocompletar = <C extends CodigoCatalogo>({
  label,
  opciones,
  valor,
  onCambio,
  cargando,
  disabled,
  required,
  helperText,
  textoSinOpciones = "Sin resultados",
}: CampoAutocompletarProps<C>) => {
  const seleccionada = opciones.find((o) => o.codigo === valor) ?? null;

  return (
    <Autocomplete
      options={opciones}
      value={seleccionada}
      onChange={(_, opcion) => onCambio(opcion ? opcion.codigo : "")}
      getOptionLabel={(o) => o.nombre}
      isOptionEqualToValue={(a, b) => a.codigo === b.codigo}
      filterOptions={(ops, estado) => {
        const buscado = normalizar(estado.inputValue.trim());
        return buscado ? ops.filter((o) => normalizar(o.nombre).includes(buscado)) : ops;
      }}
      loading={cargando}
      loadingText="Cargando…"
      noOptionsText={textoSinOpciones}
      disabled={disabled}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              minHeight: 52,
              fontSize: "0.95rem",
            },
          }}
        />
      )}
    />
  );
};
